import {
  businessForm,
  fundNecessityType,
  operationForm,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { z } from 'zod';
import { projectDetailUrlTypeEnum } from './report';

/// ////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////

export const reportLocalGovernmentFundReportType = z.enum([
  'table-A',
  'table-B',
]);
export const ReportLocalGovernmentFundReportTypeLabelModifier = {
  'table-A': t('report-local-government-fund-type-table-A'),
  'table-B': t('report-local-government-fund-type-table-B'),
} as const satisfies Record<
  z.infer<typeof reportLocalGovernmentFundReportType>,
  string
>;

export const reportLocalGovernmentFundTableACsvHeader = z.enum([
  'fundSheetNumber',
  'ministryName',
  'projectName',
  'haveAppliedLawOfAppropriatenessOfSubsidies',
  'numberOfLocalFundBuilding',
  'fiscalYearOfCreation',
  'endDate',
  'endDateOfNewApplicationReceiving',
  'operationForms',
  'businessForms',
  'overview',
  'projectDetailPageUrl',
]);

export const reportLocalGovernmentFundTableBCsvHeader = z.enum([
  'fundSheetNumber',
  'ministryName',
  'projectName',
  'beginningOfTheFundBalance',
  'nationalEquivalentOfBeginningOfTheFundBalance',
  'incomeAmount',
  'nationalEquivalentOfIncomeAmount',
  'grantedAmountByTheCountryFromInitial',
  'grantedAmountByTheCountryFromSupplementary',
  'grantedAmountByTheCountryFromReserve',
  'accountingTypeNames',
  'otherIncomeAmount',
  'businessExpense',
  'amountReturnedToTheNationalTreasury',
  'endingOfTheFundBalance',
  'nationalEquivalentOfTheEndOfTheFundBalance',
  'decidedNumberOfSubsidy',
  'decidedAmountOfSubsidy',
  'decidedNumberOfInvestment',
  'decidedAmountOfInvestment',
  'decidedNumberOfLoan',
  'decidedAmountOfLoan',
  'decidedNumberOfDebtGuarantee',
  'decidedAmountOfDebtGuarantee',
  'decidedNumberOfOthers',
  'decidedAmountOfOthers',
  'balanceNumberOfInvestment',
  'balanceAmountOfInvestment',
  'balanceNumberOfLoan',
  'balanceAmountOfLoan',
  'balanceNumberOfDebtGuarantee',
  'balanceAmountOfDebtGuarantee',
  'necessityType',
  'necessityReasons',
  'projectDetailPageUrl',
]);

export const reportLocalGovernmentFundTableFundOwnerForLocalGovernmentCsvHeader =
  z.enum([
    'number',
    'localFundOwner',
    'localFundName',
    'localFundOverview',
    'beginningOfTheFundBalance',
    'nationalEquivalentOfBeginningOfTheFundBalance',
    'incomeAmount',
    'nationalEquivalentOfIncomeAmount',
    'grantedAmountByTheCountryFromInitial',
    'grantedAmountByTheCountryFromSupplementary',
    'grantedAmountByTheCountryFromReserve',
    'otherIncomeAmount',
    'businessExpense',
    'amountReturnedToTheNationalTreasury',
    'endingOfTheFundBalance',
    'nationalEquivalentOfTheEndOfTheFundBalance',
    'decidedNumberOfSubsidy',
    'decidedAmountOfSubsidy',
    'decidedNumberOfInvestment',
    'decidedAmountOfInvestment',
    'decidedNumberOfLoan',
    'decidedAmountOfLoan',
    'decidedNumberOfDebtGuarantee',
    'decidedAmountOfDebtGuarantee',
    'decidedNumberOfOthers',
    'decidedAmountOfOthers',
    'balanceNumberOfInvestment',
    'balanceAmountOfInvestment',
    'balanceNumberOfLoan',
    'balanceAmountOfLoan',
    'balanceNumberOfDebtGuarantee',
    'balanceAmountOfDebtGuarantee',
  ]);

/// ////////////////////////////////////////
/// Model
/// ////////////////////////////////////////

export const ReportLocalGovernmentFundTableAModel = z.object({
  fundSheetNumber: z.number(),
  fundSheetBranchNumber: z.number().nullable(),
  ministryName: z.string(),
  projectName: z.string(),
  haveAppliedLawOfAppropriatenessOfSubsidies: z.boolean().nullable(),
  numberOfLocalFundBuilding: z.number().nullable(),
  fiscalYearOfCreation: z.number().nullable(),
  endDate: z.string().nullable(),
  endDateOfNewApplicationReceiving: z.string().nullable(),
  operationForms: z.array(operationForm).nullable(),
  businessForms: z.array(businessForm).nullable(),
  overview: z.string().nullable(),
  id: z.string(),
});
const LocalGovernmentTableASummaryModel = z.object({
  numberOfLocalFundBuilding: z.number().nullable(),
});
export const ReportLocalGovernmentFundsTableAModel = z.object({
  data: ReportLocalGovernmentFundTableAModel.array(),
  summaryData: LocalGovernmentTableASummaryModel,
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
});
export const ReportLocalGovernmentFundTableBModel = z.object({
  fundSheetNumber: z.number(),
  fundSheetBranchNumber: z.number().nullable(),
  ministryName: z.string(),
  projectName: z.string(),
  id: z.string(),
  accountingTypeNames: z.string().array(),
  necessityType: fundNecessityType.nullable(),
  necessityReasons: z.string().nullable(),
  beginningOfTheFundBalance: z.number().nullable(),
  rawBeginningOfTheFundBalance: z.number().nullable(),
  negativeBeginningOfTheFundBalanceCount: z.number().nullable(),
  nationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
  rawNationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
  negativeNationalEquivalentOfBeginningOfTheFundBalanceCount: z
    .number()
    .nullable(),
  incomeAmount: z.number().nullable(),
  rawIncomeAmount: z.number().nullable(),
  negativeIncomeAmountCount: z.number().nullable(),
  nationalEquivalentOfIncomeAmount: z.number().nullable(),
  rawNationalEquivalentOfIncomeAmount: z.number().nullable(),
  negativeNationalEquivalentOfIncomeAmountCount: z.number().nullable(),
  grantedAmountByTheCountryFromInitial: z.number().nullable(),
  rawGrantedAmountByTheCountryFromInitial: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromInitialCount: z.number().nullable(),
  grantedAmountByTheCountryFromSupplementary: z.number().nullable(),
  rawGrantedAmountByTheCountryFromSupplementary: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromSupplementaryCount: z
    .number()
    .nullable(),
  grantedAmountByTheCountryFromReserve: z.number().nullable(),
  rawGrantedAmountByTheCountryFromReserve: z.number().nullable(),
  negativeGrantedAmountByTheCountryFromReserveCount: z.number().nullable(),
  otherIncomeAmount: z.number().nullable(),
  rawOtherIncomeAmount: z.number().nullable(),
  negativeOtherIncomeAmountCount: z.number().nullable(),
  businessExpense: z.number().nullable(),
  rawBusinessExpense: z.number().nullable(),
  negativeBusinessExpenseCount: z.number().nullable(),
  amountReturnedToTheNationalTreasury: z.number().nullable(),
  rawAmountReturnedToTheNationalTreasury: z.number().nullable(),
  negativeAmountReturnedToTheNationalTreasuryCount: z.number().nullable(),
  endingOfTheFundBalance: z.number().nullable(),
  rawEndingOfTheFundBalance: z.number().nullable(),
  negativeEndingOfTheFundBalanceCount: z.number().nullable(),
  nationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
  rawNationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
  negativeNationalEquivalentOfTheEndOfTheFundBalanceCount: z
    .number()
    .nullable(),
  decidedNumberOfSubsidy: z.number().nullable(),
  decidedAmountOfSubsidy: z.number().nullable(),
  rawDecidedAmountOfSubsidy: z.number().nullable(),
  negativeDecidedAmountOfSubsidyCount: z.number().nullable(),
  decidedNumberOfInvestment: z.number().nullable(),
  decidedAmountOfInvestment: z.number().nullable(),
  rawDecidedAmountOfInvestment: z.number().nullable(),
  negativeDecidedAmountOfInvestmentCount: z.number().nullable(),
  decidedNumberOfLoan: z.number().nullable(),
  decidedAmountOfLoan: z.number().nullable(),
  rawDecidedAmountOfLoan: z.number().nullable(),
  negativeDecidedAmountOfLoanCount: z.number().nullable(),
  decidedNumberOfDebtGuarantee: z.number().nullable(),
  decidedAmountOfDebtGuarantee: z.number().nullable(),
  rawDecidedAmountOfDebtGuarantee: z.number().nullable(),
  negativeDecidedAmountOfDebtGuaranteeCount: z.number().nullable(),
  decidedNumberOfOthers: z.number().nullable(),
  decidedAmountOfOthers: z.number().nullable(),
  rawDecidedAmountOfOthers: z.number().nullable(),
  negativeDecidedAmountOfOthersCount: z.number().nullable(),
  balanceNumberOfInvestment: z.number().nullable(),
  balanceAmountOfInvestment: z.number().nullable(),
  rawBalanceAmountOfInvestment: z.number().nullable(),
  negativeBalanceAmountOfInvestmentCount: z.number().nullable(),
  balanceNumberOfLoan: z.number().nullable(),
  balanceAmountOfLoan: z.number().nullable(),
  rawBalanceAmountOfLoan: z.number().nullable(),
  negativeBalanceAmountOfLoanCount: z.number().nullable(),
  balanceNumberOfDebtGuarantee: z.number().nullable(),
  balanceAmountOfDebtGuarantee: z.number().nullable(),
  rawBalanceAmountOfDebtGuarantee: z.number().nullable(),
  negativeBalanceAmountOfDebtGuaranteeCount: z.number().nullable(),
});

const LocalGovernmentTableBSummaryModel = z.object({
  beginningOfTheFundBalance: z.number().nullable(),
  rawBeginningOfTheFundBalance: z.number().nullable(),
  nationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
  rawNationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
  incomeAmount: z.number().nullable(),
  rawIncomeAmount: z.number().nullable(),
  nationalEquivalentOfIncomeAmount: z.number().nullable(),
  rawNationalEquivalentOfIncomeAmount: z.number().nullable(),
  grantedAmountByTheCountryFromInitial: z.number().nullable(),
  rawGrantedAmountByTheCountryFromInitial: z.number().nullable(),
  grantedAmountByTheCountryFromSupplementary: z.number().nullable(),
  rawGrantedAmountByTheCountryFromSupplementary: z.number().nullable(),
  grantedAmountByTheCountryFromReserve: z.number().nullable(),
  rawGrantedAmountByTheCountryFromReserve: z.number().nullable(),
  otherIncomeAmount: z.number().nullable(),
  rawOtherIncomeAmount: z.number().nullable(),
  businessExpense: z.number().nullable(),
  rawBusinessExpense: z.number().nullable(),
  amountReturnedToTheNationalTreasury: z.number().nullable(),
  rawAmountReturnedToTheNationalTreasury: z.number().nullable(),
  endingOfTheFundBalance: z.number().nullable(),
  rawEndingOfTheFundBalance: z.number().nullable(),
  nationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
  rawNationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
  decidedNumberOfSubsidy: z.number().nullable(),
  decidedAmountOfSubsidy: z.number().nullable(),
  rawDecidedAmountOfSubsidy: z.number().nullable(),
  decidedNumberOfInvestment: z.number().nullable(),
  decidedAmountOfInvestment: z.number().nullable(),
  rawDecidedAmountOfInvestment: z.number().nullable(),
  decidedNumberOfLoan: z.number().nullable(),
  decidedAmountOfLoan: z.number().nullable(),
  rawDecidedAmountOfLoan: z.number().nullable(),
  decidedNumberOfDebtGuarantee: z.number().nullable(),
  decidedAmountOfDebtGuarantee: z.number().nullable(),
  rawDecidedAmountOfDebtGuarantee: z.number().nullable(),
  decidedNumberOfOthers: z.number().nullable(),
  decidedAmountOfOthers: z.number().nullable(),
  rawDecidedAmountOfOthers: z.number().nullable(),
  balanceNumberOfInvestment: z.number().nullable(),
  balanceAmountOfInvestment: z.number().nullable(),
  rawBalanceAmountOfInvestment: z.number().nullable(),
  balanceNumberOfLoan: z.number().nullable(),
  balanceAmountOfLoan: z.number().nullable(),
  rawBalanceAmountOfLoan: z.number().nullable(),
  balanceNumberOfDebtGuarantee: z.number().nullable(),
  balanceAmountOfDebtGuarantee: z.number().nullable(),
  rawBalanceAmountOfDebtGuarantee: z.number().nullable(),
});
export const ReportLocalGovernmentFundsTableBModel = z.object({
  data: ReportLocalGovernmentFundTableBModel.array(),
  summaryData: LocalGovernmentTableBSummaryModel,
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
});

const ReportLocalGovernmentFundTableFundOwnerForLocalGovernmentModel = z.object(
  {
    number: z.number(),
    localFundOwner: z.string(),
    localFundName: z.string(),
    localFundOverview: z.string(),
    beginningOfTheFundBalance: z.number().nullable(),
    nationalEquivalentOfBeginningOfTheFundBalance: z.number().nullable(),
    incomeAmount: z.number().nullable(),
    nationalEquivalentOfIncomeAmount: z.number().nullable(),
    grantedAmountByTheCountryFromInitial: z.number().nullable(),
    grantedAmountByTheCountryFromSupplementary: z.number().nullable(),
    grantedAmountByTheCountryFromReserve: z.number().nullable(),
    otherIncomeAmount: z.number().nullable(),
    businessExpense: z.number().nullable(),
    amountReturnedToTheNationalTreasury: z.number().nullable(),
    endingOfTheFundBalance: z.number().nullable(),
    nationalEquivalentOfTheEndOfTheFundBalance: z.number().nullable(),
    decidedNumberOfSubsidy: z.number().nullable(),
    decidedAmountOfSubsidy: z.number().nullable(),
    decidedNumberOfInvestment: z.number().nullable(),
    decidedAmountOfInvestment: z.number().nullable(),
    decidedNumberOfLoan: z.number().nullable(),
    decidedAmountOfLoan: z.number().nullable(),
    decidedNumberOfDebtGuarantee: z.number().nullable(),
    decidedAmountOfDebtGuarantee: z.number().nullable(),
    decidedNumberOfOthers: z.number().nullable(),
    decidedAmountOfOthers: z.number().nullable(),
    balanceNumberOfInvestment: z.number().nullable(),
    balanceAmountOfInvestment: z.number().nullable(),
    balanceNumberOfLoan: z.number().nullable(),
    balanceAmountOfLoan: z.number().nullable(),
    balanceNumberOfDebtGuarantee: z.number().nullable(),
    balanceAmountOfDebtGuarantee: z.number().nullable(),
  },
);

const LocalGovernmentTableFundOwnerForLocalGovernmentSummaryModel = z.object({
  numberOfLocalFundOwner: z.number().nullish(),
  beginningOfTheFundBalance: z.number().nullish(),
  negativeBeginningOfTheFundBalanceCount: z.number().nullish(),
  nationalEquivalentOfBeginningOfTheFundBalance: z.number().nullish(),
  negativeNationalEquivalentOfBeginningOfTheFundBalanceCount: z
    .number()
    .nullish(),
  incomeAmount: z.number().nullish(),
  negativeIncomeAmountCount: z.number().nullish(),
  nationalEquivalentOfIncomeAmount: z.number().nullish(),
  negativeNationalEquivalentOfIncomeAmountCount: z.number().nullish(),
  grantedAmountByTheCountryFromInitial: z.number().nullish(),
  negativeGrantedAmountByTheCountryFromInitialCount: z.number().nullish(),
  grantedAmountByTheCountryFromSupplementary: z.number().nullish(),
  negativeGrantedAmountByTheCountryFromSupplementaryCount: z.number().nullish(),
  grantedAmountByTheCountryFromReserve: z.number().nullish(),
  negativeGrantedAmountByTheCountryFromReserveCount: z.number().nullish(),
  otherIncomeAmount: z.number().nullish(),
  negativeOtherIncomeAmountCount: z.number().nullish(),
  businessExpense: z.number().nullish(),
  negativeBusinessExpenseCount: z.number().nullish(),
  amountReturnedToTheNationalTreasury: z.number().nullish(),
  negativeAmountReturnedToTheNationalTreasuryCount: z.number().nullish(),
  endingOfTheFundBalance: z.number().nullish(),
  negativeEndingOfTheFundBalanceCount: z.number().nullish(),
  nationalEquivalentOfTheEndOfTheFundBalance: z.number().nullish(),
  negativeNationalEquivalentOfTheEndOfTheFundBalanceCount: z.number().nullish(),
  decidedNumberOfSubsidy: z.number().nullish(),
  decidedAmountOfSubsidy: z.number().nullish(),
  negativeDecidedAmountOfSubsidyCount: z.number().nullish(),
  decidedNumberOfInvestment: z.number().nullish(),
  decidedAmountOfInvestment: z.number().nullish(),
  negativeDecidedAmountOfInvestmentCount: z.number().nullish(),
  decidedNumberOfLoan: z.number().nullish(),
  decidedAmountOfLoan: z.number().nullish(),
  negativeDecidedAmountOfLoanCount: z.number().nullish(),
  decidedNumberOfDebtGuarantee: z.number().nullish(),
  decidedAmountOfDebtGuarantee: z.number().nullish(),
  negativeDecidedAmountOfDebtGuaranteeCount: z.number().nullish(),
  decidedNumberOfOthers: z.number().nullish(),
  decidedAmountOfOthers: z.number().nullish(),
  negativeDecidedAmountOfOthersCount: z.number().nullish(),
  balanceNumberOfInvestment: z.number().nullish(),
  balanceAmountOfInvestment: z.number().nullish(),
  negativeBalanceAmountOfInvestmentCount: z.number().nullish(),
  balanceNumberOfLoan: z.number().nullish(),
  balanceAmountOfLoan: z.number().nullish(),
  negativeBalanceAmountOfLoanCount: z.number().nullish(),
  balanceNumberOfDebtGuarantee: z.number().nullish(),
  balanceAmountOfDebtGuarantee: z.number().nullish(),
  negativeBalanceAmountOfDebtGuaranteeCount: z.number().nullish(),
});

export const ReportLocalGovernmentFundsTableFundOwnerForLocalGovernmentModel =
  z.object({
    data: ReportLocalGovernmentFundTableFundOwnerForLocalGovernmentModel.array(),
    summaryData: LocalGovernmentTableFundOwnerForLocalGovernmentSummaryModel,
  });

export const ReportLocalGovernmentFundsParamsModel = z.object({
  isAllOrganization: z.boolean().nullish(),
  fiscalYear: z.any().transform((v) => Number(v)),
  projectDetailUrlType: projectDetailUrlTypeEnum,
});

/// ////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////

export const ReportLocalGovernmentFundsFilterSchema = z.object({
  fiscalYear: z.any().transform((v) => Number(v)),
  organizationIds: z
    .union([z.literal('all'), z.string().uuid()])
    .array()
    .min(1),
  reportType: reportLocalGovernmentFundReportType,
  projectDetailUrlType: projectDetailUrlTypeEnum.nullish(),
});
